import { loadProfile } from '@/embed/cache'
import { stateConvertPlanner } from '@/logic/stateReducers'
import Store from '@/store'
import { Embed, EmbedType } from '@/types/embed'
import { LevelingEmbed, LevelingEmbedRef, LevelingStepDefinition } from '@/types/leveling'
import { ReactNode } from 'react'

export type EmbedRef<E extends Embed> = {
  store?: Store
  embed: E
  active?: number
}

export function useEmbed<Type extends EmbedType>(
  stores: Record<string, Store>,
  ref: LevelingEmbedRef | undefined,
  ...types: Type[]
): EmbedRef<Embed & { type: Type }> | undefined {
  const importId = Store.useSelector((state) => state.imported?.id)
  const embed = Store.useSelector(
    (state) => {
      if (!ref || ref.id == null) return undefined
      if (ref.profile && ref.profile !== state.imported?.id) {
        return stores[ref.profile]?.getState().embeds.find((e) => e.id === ref.id)
      } else {
        return state.embeds.find((e) => e.id === ref.id)
      }
    },
    [ref, stores]
  )
  if (!embed || !ref || ref.id == null) return undefined
  if (types.length && !types.some((t) => t === embed.type)) return undefined
  return {
    store: ref.profile && ref.profile !== importId ? stores[ref.profile] : undefined,
    embed: embed as Embed & { type: Type },
    active: ref.index
  }
}

export function WithStore({ store, children }: { store: Store | undefined; children: ReactNode }) {
  if (store) {
    return <Store.Provider store={store}>{children}</Store.Provider>
  } else {
    return children
  }
}

export function levelingReferencedProfiles(leveling: LevelingEmbed, profileId?: string) {
  const profileSet = new Set<string>()
  function addRef(ref: LevelingEmbedRef | undefined) {
    if (ref && ref.profile && ref.profile !== profileId) profileSet.add(ref.profile)
  }

  for (const step of leveling.steps) {
    for (const key of Object.keys(step) as (keyof LevelingStepDefinition)[]) {
      const ref = step[key]
      if (typeof ref === 'object') addRef(ref)
    }
  }

  addRef(leveling?.overview?.ascendancy)
  addRef(leveling?.overview?.equipment)
  addRef(leveling?.overview?.progressionEquipment)
  addRef(leveling?.overview?.pantheon)
  addRef(leveling?.overview?.passives)
  addRef(leveling?.overview?.skills)

  return Array.from(profileSet)
}

export async function levelingStores(parentStore: Store) {
  const state = stateConvertPlanner(parentStore.getState())
  const leveling = state.embeds[state.activeEmbed]
  if (!leveling || leveling.type !== 'leveling') return

  const profileId = state.imported?.id
  const storesList: Record<string, Store> = {
    default: parentStore
  }
  if (profileId) storesList[profileId] = parentStore

  const referencedProfiles = levelingReferencedProfiles(leveling)
  const profiles = await Promise.all(
    referencedProfiles.map((id) => loadProfile(id).then((state) => stateConvertPlanner(state)))
  )
  referencedProfiles.forEach((id, index) => {
    storesList[id] = new Store(profiles[index])
  })

  return storesList
}
